import React from 'react'
import styled from 'styled-components'
import { StandardColumn } from '../../../../components/StandardPage'
import { OverflowCell } from '../../../../components/OverflowCell'
import { DaysOfTheWeekDiv, DayOfTheWeek } from '../../../../components/AddCompanyModal/DisponibilityComponent/style'

export const companiesScreenKey = 'companies'

export type CompanyPersonType = 'Jurídica' | 'Física' | 'Própria' | 'Transportadora' | null

export interface CompanyRow {
  id: number
  cnpj?: string
  cpf?: string
  companyName?: string
  contactName?: string
  stateRegistration?: string
  phone?: string
  email?: string
  isMain?: boolean
  tipoEstabelecimento?: string
  companyTypes?: unknown[]
  street?: string
  number?: string
  neighborhood?: string
  city?: string
  state?: string
  cep?: string
  activedDays?: number[]
  startTime?: string
  endTime?: string
  contribuinteIcms?: string
  contacts?: { name?: string; phone?: string; email?: string }[]
  linkedUsers?: { name?: string }[]
}

const MatrizFilialBadge = styled.span`
  background-color: #e0f2fe;
  color: #0369a1;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  flex-shrink: 0;
`

const NameWithBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;

  span:last-child {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`

const daysLabels = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']

const formatTime = (time: unknown): string => {
  if (!time) return '--'
  try {
    const parsed = new Date(time as string)
    if (!isNaN(parsed.getTime())) {
      return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    return String(time)
  } catch {
    return String(time)
  }
}

const getMatrizFilialLabel = (company: CompanyRow): string => {
  if (!company.isMain) return ''
  if (company.tipoEstabelecimento) return company.tipoEstabelecimento
  if (!company.cnpj) return 'Matriz'
  return company.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial'
}

const renderNameWithBadge = (company: CompanyRow, name: string) => {
  const badgeLabel = getMatrizFilialLabel(company)
  return (
    <NameWithBadge>
      {badgeLabel && <MatrizFilialBadge>{badgeLabel}</MatrizFilialBadge>}
      <span>{name}</span>
    </NameWithBadge>
  )
}

const computeTypeLabel = (company: CompanyRow): string => {
  const types = company.companyTypes || []
  if (Array.isArray(types)) {
    return types
      .map((entry: any) => {
        if (typeof entry === 'string') return entry
        if (typeof entry === 'number') return String(entry)
        return entry?.typeName || entry?.companyType?.typeName || ''
      })
      .filter(Boolean)
      .join(', ')
  }
  try {
    return String(types)
  } catch {
    return ''
  }
}

const formatAddress = (company: CompanyRow): string => {
  const safe = (value: unknown): string => {
    if (!value && value !== 0) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'number') return String(value)
    if (typeof value === 'object') {
      const record = value as Record<string, string>
      return record.name || record.label || record.typeName || ''
    }
    return String(value)
  }

  const street = safe(company.street)
  const number = safe(company.number)
  const neighborhood = safe(company.neighborhood)
  const city = safe(company.city)
  const state = safe(company.state)
  const cepDigits = String(company.cep || '').replace(/\D/g, '')
  const cepPadded = cepDigits ? cepDigits.padStart(8, '0') : ''
  const cepFormatted = cepPadded ? `${cepPadded.slice(0, 5)}-${cepPadded.slice(5)}` : ''
  const parts: string[] = []
  if (street) parts.push(`${street}${number ? `, ${number}` : ''}`)
  if (neighborhood) parts.push(neighborhood)
  if (city) parts.push(city)
  if (state) parts.push(state)
  const main = parts.join(' - ')
  const result = cepFormatted ? `${main}${main ? ' - ' : ''}${cepFormatted}` : main
  return result.trim() ? result : ''
}

const formatContribuinteIcms = (value: unknown): string => {
  if (value === 'CONTRIBUINTE') return 'Contribuinte'
  if (value === 'ISENTO') return 'Isento'
  if (value === 'NAO_CONTRIBUINTE') return 'Não Contribuinte'
  return value ? String(value) : ''
}

const contactsSummary = (row: CompanyRow): string =>
  (row.contacts || []).map((contact) => contact.name || '').filter(Boolean).join(', ')

const contactsTooltip = (row: CompanyRow): string =>
  (row.contacts || [])
    .map((contact) => {
      const parts = [contact.name || '']
      if (contact.phone) parts.push(contact.phone)
      if (contact.email) parts.push(contact.email)
      return parts.join(' - ')
    })
    .join('\n')

const linkedUsersSummary = (row: CompanyRow): string =>
  (row.linkedUsers || []).map((user) => user.name || '').filter(Boolean).join(', ')

const buildIdentityColumns = (personType: CompanyPersonType): StandardColumn<CompanyRow>[] => {
  if (personType === 'Jurídica') {
    return [
      {
        id: 'cnpj',
        label: 'CNPJ',
        accessor: (row) => (row.cnpj || '').toString().trim(),
      },
      {
        id: 'companyName',
        label: 'Razão Social',
        accessor: (row) => (row.companyName || '').toString().trim(),
        cell: (row) => renderNameWithBadge(row, (row.companyName || '').toString().trim()),
      },
      {
        id: 'stateRegistration',
        label: 'Inscrição Estadual',
        accessor: (row) => (row.stateRegistration || '').toString().trim(),
      },
    ]
  }
  if (personType === 'Física') {
    return [
      {
        id: 'cpf',
        label: 'CPF',
        accessor: (row) => (row.cpf || '').toString().trim(),
      },
      {
        id: 'companyName',
        label: 'Nome',
        accessor: (row) =>
          (row.contactName || '').toString().trim() || (row.companyName || '').toString().trim(),
      },
    ]
  }
  return [
    {
      id: 'doc',
      label: 'CNPJ/CPF',
      accessor: (row) =>
        (row.cnpj || '').toString().trim() || (row.cpf || '').toString().trim(),
    },
    {
      id: 'name',
      label: 'Razão Social / Nome',
      accessor: (row) =>
        (row.companyName || '').toString().trim() || (row.contactName || '').toString().trim(),
      cell: (row) =>
        renderNameWithBadge(
          row,
          (row.companyName || '').toString().trim() || (row.contactName || '').toString().trim(),
        ),
    },
  ]
}

export const buildCompaniesColumns = (personType: CompanyPersonType): StandardColumn<CompanyRow>[] => [
  ...buildIdentityColumns(personType),
  {
    id: 'phone',
    label: 'Telefone',
  },
  {
    id: 'email',
    label: 'E-mail',
    cell: (row) => {
      const email = (row.email || '').toString()
      const emailList = email.split(',').map((entry) => entry.trim()).filter(Boolean)
      return (
        <OverflowCell
          text={emailList.join('\n')}
          forceTooltip={emailList.length > 1}
          maxWidth={150}
        >
          {email || 'Nenhum e-mail'}
        </OverflowCell>
      )
    },
  },
  {
    id: 'companyTypes',
    label: 'Tipo',
    accessor: (row) => computeTypeLabel(row),
  },
  {
    id: 'address',
    label: 'Endereço',
    defaultHidden: true,
    accessor: (row) => formatAddress(row),
    cell: (row) => {
      const addressText = formatAddress(row)
      return (
        <OverflowCell text={addressText} maxWidth={150}>
          {addressText || 'Nenhum endereço cadastrado'}
        </OverflowCell>
      )
    },
  },
  {
    id: 'activedDays',
    label: 'Funcionamento',
    defaultHidden: true,
    sortable: false,
    exportValue: (row) =>
      `${(row.activedDays || [])
        .slice()
        .sort((a, b) => a - b)
        .map((dayIndex) => daysLabels[dayIndex] ?? dayIndex)
        .join(' ')} ${formatTime(row.startTime)} - ${formatTime(row.endTime)}`.trim(),
    cell: (row) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.2rem' }}>
        <DaysOfTheWeekDiv>
          {(row.activedDays || [])
            .slice()
            .sort((a, b) => a - b)
            .map((dayIndex) => (
              <DayOfTheWeek key={dayIndex} className='actived'>
                {daysLabels[dayIndex] ?? dayIndex}
              </DayOfTheWeek>
            ))}
        </DaysOfTheWeekDiv>
        <div style={{ fontSize: '.8rem', color: '#666', marginTop: '2px' }}>
          {formatTime(row.startTime)} - {formatTime(row.endTime)}
        </div>
      </div>
    ),
  },
  {
    id: 'contribuinteIcms',
    label: 'Contribuinte ICMS',
    defaultHidden: true,
    accessor: (row) => formatContribuinteIcms(row.contribuinteIcms),
  },
  {
    id: 'contacts',
    label: 'Contatos',
    defaultHidden: true,
    accessor: (row) => contactsSummary(row),
    cell: (row) => {
      const summary = contactsSummary(row)
      if (!summary) return 'Nenhum'
      return (
        <OverflowCell text={contactsTooltip(row)} forceTooltip maxWidth={150}>
          {summary}
        </OverflowCell>
      )
    },
  },
  {
    id: 'linkedUsers',
    label: 'Usuários Vinculados',
    defaultHidden: true,
    accessor: (row) => linkedUsersSummary(row),
    cell: (row) => {
      const summary = linkedUsersSummary(row)
      if (!summary) return 'Nenhum'
      return (
        <OverflowCell
          text={(row.linkedUsers || []).map((user) => user.name || '').filter(Boolean).join('\n')}
          forceTooltip
          maxWidth={150}
        >
          {summary}
        </OverflowCell>
      )
    },
  },
]
